/**
 * PainelDrilldownModal — Dialog M3 de detalhes de KPI.
 * Fiel ao prototipo dashboard-m3-expressive.html (secao .modal / .overlay).
 * Escopo: exclusivo da tela Painel.
 *
 * A11y: role dialog, aria-modal, aria-labelledby, foco retido, ESC fecha.
 */

import { useEffect, useRef } from 'react';
import type { PainelSourceKey } from './PainelFunnelCard';

// ---------------------------------------------------------------------------
// Tipos
// ---------------------------------------------------------------------------

export interface PainelDrilldownItem {
  readonly id: string;
  readonly label: string;
  readonly value: string | null;
  readonly source: PainelSourceKey;
  readonly sourceLabel: string;
  readonly note?: string | undefined;
  readonly period?: string | undefined;
  readonly account?: string | undefined;
  readonly collectedAt?: string | undefined;
}

export interface PainelDrilldownModalProps {
  readonly item: PainelDrilldownItem | null;
  /** Callback ao fechar. */
  readonly onClose: () => void;
  /** Strings i18n — evita hardcode no componente. */
  readonly strings: {
    readonly sourceLabel: string; // "Fonte"
    readonly accountLabel: string; // "Conta"
    readonly periodLabel: string; // "Periodo"
    readonly collectedLabel: string; // "Coletado"
    readonly closeBtnLabel: string; // "Fechar"
    readonly originLabel: string; // "Origem do dado"
    readonly ctaLabel: string; // "Perguntar a CROSS sobre isso"
    readonly noDataLabel: string; // "—"
  };
  /** Callback do CTA "Perguntar a CROSS" — opcional. */
  readonly onAsk?: () => void;
}

// ---------------------------------------------------------------------------
// Componente
// ---------------------------------------------------------------------------

export function PainelDrilldownModal({
  item,
  onClose,
  strings,
  onAsk,
}: PainelDrilldownModalProps): JSX.Element | null {
  const closeRef = useRef<HTMLButtonElement>(null);
  const titleId = 'painel-modal-title';

  // Foco no botao fechar ao abrir
  useEffect(() => {
    if (item !== null) {
      closeRef.current?.focus();
    }
  }, [item]);

  // ESC fecha
  useEffect(() => {
    if (item === null) return;
    const handler = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [item, onClose]);

  if (item === null) return null;

  const displayValue =
    item.value === null || item.value === undefined || item.value === 'N/A'
      ? strings.noDataLabel
      : item.value;

  return (
    <div className="painel-overlay" role="presentation" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="painel-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="painel-modal-header">
          <div className="painel-modal-label">{strings.originLabel}</div>
          <div id={titleId} className="painel-modal-title">
            {item.label}
          </div>
          <div className="painel-modal-value">{displayValue}</div>
          <button
            ref={closeRef}
            type="button"
            className="painel-modal-close"
            onClick={onClose}
            aria-label={strings.closeBtnLabel}
          >
            <span
              className="material-symbols-rounded"
              style={{ fontSize: 20, lineHeight: 1 }}
              aria-hidden="true"
            >
              close
            </span>
          </button>
        </div>

        <div className="painel-modal-body">
          <dl className="painel-modal-dl">
            <dt className="painel-modal-dt">{strings.sourceLabel}</dt>
            <dd className="painel-modal-dd">{item.sourceLabel}</dd>

            {item.account !== undefined ? (
              <>
                <dt className="painel-modal-dt">{strings.accountLabel}</dt>
                <dd className="painel-modal-dd">{item.account}</dd>
              </>
            ) : null}

            {item.period !== undefined ? (
              <>
                <dt className="painel-modal-dt">{strings.periodLabel}</dt>
                <dd className="painel-modal-dd">{item.period}</dd>
              </>
            ) : null}

            {item.collectedAt !== undefined ? (
              <>
                <dt className="painel-modal-dt">{strings.collectedLabel}</dt>
                <dd className="painel-modal-dd">{item.collectedAt}</dd>
              </>
            ) : null}
          </dl>

          {item.note !== undefined && item.note.length > 0 ? (
            <div className="painel-modal-note">{item.note}</div>
          ) : null}

          {onAsk !== undefined ? (
            <button
              type="button"
              className="painel-modal-cta"
              onClick={() => {
                onAsk();
                onClose();
              }}
            >
              {strings.ctaLabel}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
